import React, { useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Card, SkipNav } from "@cmsgov/ds-medicare-gov";
import {
  ActionType,
  PharmacyType,
  PlanCategoryType,
  SearchResultPlan,
} from "../../@types";
import PlanCardHeader from "./PlanCardHeader";
import PlanCardActions from "./PlanCardActions";
import PlanBenefits from "./PlanBenefits";
import MonthlyPremium from "./MonthlyPremium";
import OtherCosts from "./OtherCosts";
import Drugs from "./Drugs";
import {
  CONSTANTS,
  isMedicareAdvantageType,
  useCosts,
  useTranslate,
  PreviewContext,
  shouldHideCompare,
} from "../../helpers";
import Pharmacies from "./Pharmacies";
import Deductible from "./Deductible";
import Copays from "./Copays";
import YearlyCost from "./YearlyCost";
import { AppContext } from "../../app/store";
import { CollapsibleBox } from "..";
import routes from "../../app/routes";
import { Ga4Event } from "../../app/contexts/Analytics/types";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";

interface PlanCardProps {
  plan: SearchResultPlan;
  handleAddToCompare?: (plan: SearchResultPlan) => void;
  handleRemoveFromCompare?: (removeId: number | string) => void;
  isCurrentPlan?: boolean;
  labelId?: string;
  dataTestId?: string;
}

const PlanCard: React.FunctionComponent<PlanCardProps> = ({
  dataTestId = "",
  handleAddToCompare,
  handleRemoveFromCompare,
  isCurrentPlan,
  labelId = "plan_card.your_current_plan",
  plan,
}) => {
  const t = useTranslate();
  const {
    state: { beneficiary, language, planType, compareList },
    dispatch,
  } = useContext(AppContext);
  const previewMode = useContext(PreviewContext);
  const ref = useRef(null);
  const drugCostInfo = useCosts(plan, ref);
  const hideCompareButton = shouldHideCompare({
    isCurrentPlan,
    planType,
    plan,
  });
  const { search } = useLocation();
  const pdes = beneficiary ? beneficiary.pdes : [];
  const drugSelectionRouteBase =
    pdes && pdes.length > 0 ? routes.pdePage : routes.managePrescriptions;
  const drugSelectionRoute = `${drugSelectionRouteBase}${search}`;
  const compareAnchorTarget = "plan-compare-footer-sticky-tray-heading";

  const onGoToDrugSelection = (): void => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.VIEW_DRUG_LIST,
        text: "Add your prescription drug",
      },
    });
    dispatch({
      type: ActionType.UPDATE_PHARMACY_TYPE,
      payload: PharmacyType.MAIL_AND_RETAIL,
    });
  };

  return (
    <Card
      className={`PlanCard ${plan.plan_type}${
        previewMode ? " preview-mode" : ""
      }`}
      data-testid={dataTestId}
    >
      {isCurrentPlan ? (
        <div
          className="PlanCard__current_plan_banner"
          data-cy="plan-card-current-plan-banner"
          data-testid={`${dataTestId}-banner`}
        >
          {t(labelId)}
        </div>
      ) : null}
      <PlanCardHeader plan={plan} />
      {plan.category === PlanCategoryType.MSA && (
        <p>
          <a
            href={CONSTANTS.linkToWhatIsMSA(language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("msa_plan.how_does_msa_work")}
          </a>
        </p>
      )}

      <div ref={ref} className="PlanCard__body">
        <div>
          <div className="PlanCard__flex_left">
            <div>
              <MonthlyPremium plan={plan} />
              {!previewMode && (
                <YearlyCost plan={plan} drugCostInfo={drugCostInfo} />
              )}
              {isMedicareAdvantageType(plan.plan_type) ? (
                <OtherCosts plan={plan} />
              ) : (
                <Deductible plan={plan} />
              )}
            </div>
            {!previewMode && (
              <PlanCardActions
                plan={plan}
                isCurrentPlan={isCurrentPlan}
                handleAddToCompare={handleAddToCompare}
                handleRemoveFromCompare={handleRemoveFromCompare}
                hideCompareButton={hideCompareButton}
              />
            )}
          </div>
          <div className="PlanCard__flex_right">
            <CollapsibleBox
              labelId="plan_card.view_more_information"
              containerClassname="PlanCard__more_info"
              titleClassname="PlanCard__more_info_trigger ds-c-button ds-c-button--ghost mct-hide-print"
              open={previewMode}
            >
              {isMedicareAdvantageType(plan.plan_type) ? (
                <>
                  <PlanBenefits plan={plan} />
                  <Copays plan={plan} />
                </>
              ) : (
                <Pharmacies
                  plan={plan}
                  drugCostInfo={drugCostInfo}
                  drugSelectionRoute={drugSelectionRoute}
                  onGoToDrugSelection={onGoToDrugSelection}
                />
              )}
              <Drugs
                plan={plan}
                drugSelectionRoute={drugSelectionRoute}
                onGoToDrugSelection={onGoToDrugSelection}
              />
            </CollapsibleBox>
          </div>
        </div>
      </div>
      {!!compareList.length && (
        <SkipNav
          href={`#${compareAnchorTarget}`}
          onClick={(e): void => {
            e.preventDefault();
            document.getElementById(compareAnchorTarget)?.focus();
          }}
        >
          {t("planCard_skip_text")}
        </SkipNav>
      )}
    </Card>
  );
};

export default PlanCard;
